import React, { useState } from "react";
import { Play, Pause, Video, X, Music } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { VideoFacade } from "@/components/site/VideoFacade";

// Turn a track object (from a release's tracklist) into what PlayerContext expects.
export const trackToPlayable = (track, release) => {
  if (!track || !track.preview_url) return null;
  return {
    id: `${release.id}:${track.number || 0}:${track.title || ""}`,
    title: track.title,
    artist: release.artist || "P. Frazy",
    artwork: mediaUrl(track.artwork_url) || mediaUrl(release.artwork_url) || "/assets/img/logo.png",
    src: mediaUrl(track.preview_url),
    peaks: track.peaks || [],
    bpm: 90,
    full: Boolean(release.full_song),
  };
};

// Convert a track's video fields to the shape VideoFacade expects.
export const trackVideo = (track) => {
  if (!track) return null;
  if (track.video_url) {
    const url = track.video_url;
    let source_type = "youtube";
    if (/vimeo\.com/i.test(url)) source_type = "vimeo";
    else if (/youtu/i.test(url)) source_type = "youtube";
    else source_type = "upload";
    return { id: `t-${track.number || 0}`, title: track.title || "Music video", url, source_type };
  }
  if (track.video_media_id && track.video_file_url) {
    return {
      id: `t-${track.number || 0}`,
      title: track.title || "Music video",
      url: track.video_file_url,
      file_url: track.video_file_url,
      poster_url: track.video_poster_url,
      source_type: "upload",
    };
  }
  return null;
};

// Modal that hosts a VideoFacade so audio auto-pauses and user can close it.
export const VideoModal = ({ video, onClose }) => {
  if (!video) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="video-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 90,
        background: "rgba(0,0,0,.86)", backdropFilter: "blur(6px)",
        display: "grid", placeItems: "center", padding: "1.5rem",
      }}
    >
      <div style={{ position: "relative", width: "min(1080px, 100%)" }} onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          aria-label="Close video"
          data-testid="video-modal-close"
          className="font-caps"
          style={{
            position: "absolute", top: -44, right: 0,
            display: "inline-flex", alignItems: "center", gap: 6,
            color: "var(--parchment)", background: "transparent",
            border: "none", cursor: "pointer", fontSize: ".72rem",
          }}
        >
          <X size={16} /> Close
        </button>
        <VideoFacade video={video} big />
      </div>
    </div>
  );
};

/**
 * Shared row/card for a single track. Used both in album detail page and in inline tracklists.
 */
export const TrackRow = ({ release, track, index, compact = false }) => {
  const player = usePlayer();
  const [showVideo, setShowVideo] = useState(false);

  const playable = trackToPlayable(track, release);
  const video = trackVideo(track);
  const hasAudio = Boolean(playable);
  const hasVideo = Boolean(video);
  const full = Boolean(release.full_song);
  const isActive = playable && player.isActive(playable.id);
  const playing = isActive && player.isPlaying;

  const onToggle = () => {
    if (!playable) return;
    if (isActive) player.toggle();
    else player.playTrack(playable, 0);
  };

  const artwork = mediaUrl(track.artwork_url) || mediaUrl(release.artwork_url) || "/assets/img/logo.png";
  const num = String(track.number || index + 1).padStart(2, "0");

  return (
    <>
      <div
        className="flex items-center gap-4 p-3"
        style={{ borderRadius: "var(--radius)", border: "var(--rule)", background: "var(--ink-raised)" }}
        data-testid={`track-row-${index}`}
      >
        <span className="font-caps text-ash" style={{ fontSize: ".62rem", width: 24 }}>{num}</span>

        <div className="relative shrink-0" style={{ width: compact ? 48 : 64, height: compact ? 48 : 64, borderRadius: "var(--radius)", overflow: "hidden", background: "#000", border: "1px solid rgba(200,155,60,.25)" }}>
          {artwork ? <img src={artwork} alt="" className="w-full h-full object-cover" /> : <Music size={20} color="var(--ash)" />}
          {playing && (
            <span className="absolute top-1 left-1 h-1.5 w-1.5 rounded-full candle-glow" style={{ background: "var(--rose)" }} />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="truncate" style={{ color: "var(--parchment)", fontSize: compact ? ".9rem" : "1rem" }}>{track.title || "Untitled"}</p>
          <p className="font-caps text-ash mt-1" style={{ fontSize: ".55rem" }}>
            {hasAudio ? (full ? "Full song" : "Preview available") : (full ? "Audio coming soon" : "Preview coming soon")}{hasVideo ? " · Video" : ""}
          </p>
        </div>

        <div className="flex gap-2 shrink-0">
          {hasAudio && (
            <button
              onClick={onToggle}
              className={playing ? "btn-gold" : "btn-ghost"}
              style={{ padding: ".55rem .9rem", fontSize: ".62rem" }}
              data-testid={`track-preview-${index}`}
              aria-label={playing ? "Pause" : (full ? "Play song" : "Play preview")}
            >
              {playing ? <Pause size={12} fill="currentColor" /> : <Play size={12} fill="currentColor" />}
              {playing ? "Pause" : (full ? "Play" : "Preview")}
            </button>
          )}
          {hasVideo && (
            <button
              onClick={() => setShowVideo(true)}
              className="btn-ghost"
              style={{ padding: ".55rem .9rem", fontSize: ".62rem" }}
              data-testid={`track-watch-${index}`}
              aria-label={`Watch ${track.title} music video`}
            >
              <Video size={12} /> Watch
            </button>
          )}
        </div>
      </div>
      {showVideo && <VideoModal video={video} onClose={() => setShowVideo(false)} />}
    </>
  );
};
